import {
  DEFAULT_PIP_CORNER,
  type PipCorner,
  isPipCorner,
} from '@goaltree/backend/convex/lib/pipSnap'
import type { Viewing } from '@goaltree/backend/convex/lib/viewingContext'
import { useEffect } from 'react'
import { create } from 'zustand'

import type { PlaybookContext } from '@/components/coach-chat-sheet'

// The app-wide coach sheet and its live call. CoachCallHost (mounted once at the
// root) owns the call session, so it survives navigation and closing the sheet.
// Minimised, a phone with the bottom nav shows the controls in a pill over the
// nav coach; everywhere else the call shrinks to a floating card.

const CORNER_KEY = 'coachCall.pipCorner'

function savedCorner(): PipCorner {
  try {
    const v = localStorage.getItem(CORNER_KEY)
    return isPipCorner(v) ? v : DEFAULT_PIP_CORNER
  } catch {
    return DEFAULT_PIP_CORNER
  }
}

// What the bottom-nav pill needs from the one call session. The host publishes it;
// the nav only reads it, so the session stays mounted in one place.
export type NavCall = {
  name: string
  connecting: boolean
  elapsedSeconds: number
  muted: boolean
  caption: string | null
  faceVideo: HTMLVideoElement | null
  coachSpeaking: boolean
  videoOn: boolean
  canVideo: boolean
  toggleMute: () => void
  toggleVideo: () => void
  hangUp: () => void
}

export type CoachSheetRequest = {
  message?: string
  playbookContext?: PlaybookContext
  startCall?: boolean
  // With startCall: video on or off for this call; unset follows the saved pref.
  callVideo?: boolean
  // Changes on every start so a second tap starts another call after the last one ends.
  callNonce?: number
}

type CoachCallState = {
  sheet: CoachSheetRequest & { open: boolean }
  mode: 'full' | 'pip'
  // The full view is the fullscreen overlay (a call started from a FAB or ring)
  // rather than the call inside the sheet.
  fullscreen: boolean
  active: boolean
  corner: PipCorner
  // What's open on screen during a call, for the coach (lib/viewingContext);
  // the last is on top (a task sheet over its goal page).
  viewing: Viewing[]
  // The coach hears which screen is open. Settings writes this (unset = on).
  shareViewing: boolean
  // True while a BottomNav bar is actually on screen (not the onboarding early return).
  bottomNav: boolean
  // Set while the minimised call should use the nav pill instead of the floating card.
  navCall: NavCall | null
  openSheet: (req?: CoachSheetRequest | null) => void
  closeSheet: () => void
  minimize: () => void
  expand: () => void
  setFullscreen: (fullscreen: boolean) => void
  setActive: (active: boolean) => void
  setCorner: (corner: PipCorner) => void
  addViewing: (viewing: Viewing) => void
  removeViewing: (key: string) => void
}

export const useCoachCall = create<CoachCallState>((set, get) => ({
  sheet: { open: false },
  mode: 'full',
  fullscreen: false,
  active: false,
  corner: typeof window === 'undefined' ? DEFAULT_PIP_CORNER : savedCorner(),
  viewing: [],
  shareViewing: true,
  bottomNav: false,
  navCall: null,
  // A bare `new CustomEvent('open-coach')` has detail null, not undefined.
  openSheet: (request) => {
    const req = request ?? {}
    // One call at a time: asking for a call while one is live just shows it.
    if (get().active) {
      get().expand()
      return
    }
    set({ sheet: { ...req, open: true }, mode: 'full', ...(req.startCall && { fullscreen: true }) })
  },
  closeSheet: () => set({ sheet: { open: false }, mode: 'full' }),
  minimize: () => set({ sheet: { open: false }, mode: 'pip', fullscreen: false }),
  expand: () => set({ sheet: { open: true }, mode: 'full', fullscreen: true }),
  setFullscreen: (fullscreen) => set({ fullscreen }),
  setActive: (active) => set(active ? { active } : { active, mode: 'full' }),
  setCorner: (corner) => {
    try {
      localStorage.setItem(CORNER_KEY, corner)
    } catch {
      // storage unavailable — keep it for this session
    }
    set({ corner })
  },
  addViewing: (v) =>
    set((s) => ({
      viewing: s.viewing.some((x) => x.key === v.key)
        ? s.viewing.map((x) => (x.key === v.key ? v : x))
        : [...s.viewing, v],
    })),
  removeViewing: (key) => set((s) => ({ viewing: s.viewing.filter((x) => x.key !== key) })),
}))

// More than one screen can mount a bar during a route change. Count them so the
// last one to leave is what clears the flag.
let bottomNavMounts = 0

export function setBottomNavMounted(mounted: boolean) {
  bottomNavMounts = Math.max(0, bottomNavMounts + (mounted ? 1 : -1))
  const bottomNav = bottomNavMounts > 0
  if (useCoachCall.getState().bottomNav !== bottomNav) useCoachCall.setState({ bottomNav })
}

/** Tells a live call's coach what this screen shows; a no-op when no call is on. */
export function useReportViewing(build: () => Viewing | null) {
  const active = useCoachCall((s) => s.active)
  const viewing = active ? build() : null
  const key = viewing?.key
  const text = viewing?.text
  useEffect(() => {
    if (!key) return
    return () => useCoachCall.getState().removeViewing(key)
  }, [key])
  useEffect(() => {
    if (key && text) useCoachCall.getState().addViewing({ key, text })
  }, [key, text])
}
